import { useCallback, useEffect, useRef, useState } from 'react'
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type Notification,
} from './api'
import { connectNotificationStream, type LiveNotification } from './stream'

export interface NotificationCentre {
  notifications: Notification[]
  unread: number
  /** Есть ли живая связь — для индикатора, а не для блокировки интерфейса. */
  connected: boolean
  /** Первая загрузка ленты ещё идёт. */
  loading: boolean
  /** Лента не загрузилась; живой канал при этом может работать. */
  error: unknown
  markRead: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
  reload: () => void
}

/**
 * Центр уведомлений с живой доставкой (тикет #49).
 *
 * ## Список приходит запросом, обновления — потоком
 *
 * Канал доставляет только то, что случилось при открытой странице. Всё, что накопилось
 * раньше, приходит обычным запросом при монтировании. Обратный порядок — «канал как
 * источник истины» — теряет уведомления для всякого, кто в этот момент не смотрел на
 * экран, и теряет молча.
 */
export function useNotifications(): NotificationCentre {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unread, setUnread] = useState(0)
  const [connected, setConnected] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)

  // Ref, а не состояние: обработчик потока создаётся один раз и через замыкание видел
  // бы первый снимок списка. Симптом — новое уведомление затирает все предыдущие.
  const known = useRef(new Set<string>())

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)

    fetchNotifications(false, controller.signal)
      .then((feed) => {
        known.current = new Set(feed.notifications.map((n) => n.id))
        setNotifications(feed.notifications)
        setUnread(feed.unread)
        setError(null)
        setLoading(false)
      })
      .catch((failure: unknown) => {
        // Без модальной ошибки на весь экран: хук отдаёт сбой состоянием, а экран
        // решает, как его назвать. Отмену при размонтировании ошибкой не считаем.
        if (controller.signal.aborted) return
        setError(failure)
        setLoading(false)
      })

    return () => controller.abort()
  }, [reloadToken])

  useEffect(() => {
    const stop = connectNotificationStream({
      onUnreadCount: setUnread,
      onStateChange: setConnected,
      onNotification: (live: LiveNotification) => {
        // Доставка at-least-once по своей природе: переподключение может принести то,
        // что уже показано. Повтор молча отбрасывается — дубль в списке читается как
        // два разных события.
        if (known.current.has(live.id)) {
          setUnread(live.unread)
          return
        }
        known.current.add(live.id)

        setNotifications((current) => [
          {
            id: live.id,
            title: live.title,
            body: live.body,
            kind: live.kind,
            entityType: live.entityType,
            entityId: live.entityId,
            read: false,
            createdAt: live.createdAt,
          },
          ...current,
        ])
        setUnread(live.unread)
      },
    })

    return stop
  }, [])

  const markRead = useCallback(async (id: string) => {
    // Оптимистично: отметка о прочтении — действие без последствий, и ждать ответа
    // сервера, чтобы погасить точку, значит показывать задержку там, где её не ждут.
    setNotifications((current) =>
      current.map((n) => (n.id === id ? { ...n, read: true } : n)),
    )
    setUnread((current) => Math.max(0, current - 1))

    try {
      const { unread: actual } = await markNotificationRead(id)
      setUnread(actual)
    } catch {
      // Сервер не принял — вернуть точку на место, иначе пользователь считает
      // уведомление обработанным, а оно висит.
      setNotifications((current) =>
        current.map((n) => (n.id === id ? { ...n, read: false } : n)),
      )
      setUnread((current) => current + 1)
    }
  }, [])

  const markAllRead = useCallback(async () => {
    const { unread: actual } = await markAllNotificationsRead()
    setNotifications((current) => current.map((n) => ({ ...n, read: true })))
    setUnread(actual)
  }, [])

  const reload = useCallback(() => setReloadToken((token) => token + 1), [])

  return { notifications, unread, connected, loading, error, markRead, markAllRead, reload }
}
